import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import { Config } from "@config";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { EventComponentType, GA4EventParameters } from "@utils/google-analytics-4.type";
import { getUrlParameter } from "bm-ui-kit";
import { type FC, type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

interface EventMetadata {
	instanceId?: string;
	calculationId?: string;
	buttonId?: string;
	leadId?: string | number;
	productTypes: ProductType[];
	componentType: EventComponentType;
	componentName: string;
}

interface EventTrackingContextType {
	// Global parameters
	contextMetadata?: EventMetadata;
	wasVisible: boolean;

	// Callbacks
	sendEvent: (event: string, model?: GA4EventParameters) => Promise<void>;
}

const EventTrackingContext = createContext<EventTrackingContextType>({
	contextMetadata: {
		instanceId: undefined,
		calculationId: undefined,
		buttonId: undefined,
		leadId: undefined,
		productTypes: undefined,
		componentType: undefined,
		componentName: undefined,
	},
	wasVisible: false,
	sendEvent: async (event: string, model?: GA4EventParameters) => {
		const { instanceId = GoogleAnalytics4.generateId("id"), componentName = "", ...other } = model || {};
		const parameters = {
			instanceId: instanceId,
			componentName: componentName,
			...other,
		};

		GoogleAnalytics4.sendEvent(event, parameters);
	},
});

interface EventTrackingProviderProps<P, R extends HTMLElement = HTMLElement> {
	metadata: EventMetadata;
	disableImpressionTracking?: boolean;
	children: (props: P, ref: React.LegacyRef<R>) => ReactNode;
	props?: P;
}

const isJavaScriptEmbedded = String(getUrlParameter("embedJs", "0")) === "1";

const EventTrackingProvider = <P, R extends HTMLElement>({
	children,
	metadata,
	disableImpressionTracking,
	props,
}: EventTrackingProviderProps<P, R>) => {
	const { contextMetadata: parentMetadata } = useEventTracking() || { contextMetadata: undefined };
	const [wasVisible, setWasVisible] = useState(!!disableImpressionTracking);
	const instanceId = useMemo(
		() => metadata.instanceId || parentMetadata?.instanceId || GoogleAnalytics4.generateId("id"),
		[metadata.instanceId, parentMetadata?.instanceId],
	);
	const componentName = useMemo(
		() => [parentMetadata?.componentName, metadata.componentName].filter(Boolean).join("/"),
		[parentMetadata?.componentName, metadata.componentName],
	);

	const sendEvent = useCallback(
		async (event: string, parameters?: GA4EventParameters) => {
			GoogleAnalytics4.sendEvent(event, {
				...parentMetadata,
				...metadata,
				...parameters,
				instanceId: parameters?.instanceId || instanceId,
				productTypes: parameters?.productTypes || metadata.productTypes.join(","),
				componentName,
			});
		},
		[instanceId, componentName, parentMetadata, metadata],
	);

	const value: EventTrackingContextType = useMemo(
		() => ({
			contextMetadata: {
				...parentMetadata,
				instanceId,
				...metadata,
				componentName,
			},
			wasVisible,
			sendEvent,
		}),
		[wasVisible, parentMetadata, instanceId, metadata, componentName, sendEvent],
	);

	const { ref, inView } = useInView({
		...GoogleAnalytics4.getInViewProps(),
		skip: isJavaScriptEmbedded,
		triggerOnce: true,
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: only used in the initial render
	useEffect(
		() => {
			if (!parentMetadata?.componentName) {
				sendEvent("BM_componentCreate");

				if (!Config.isSsr() && isJavaScriptEmbedded) {
					const listener = (event: MessageEvent) => {
						if (event.data === "impression") {
							handleVisibilityChange(true);
						}
					};

					window.addEventListener("message", listener, false);

					return () => {
						window.removeEventListener("message", listener, false);
					};
				}
			}
		},
		[
			/* BE EMPTY */
		],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: depends only inView change
	useEffect(() => {
		if (inView && !disableImpressionTracking) {
			setWasVisible(inView);

			if (!parentMetadata?.componentName) {
				sendEvent("BM_componentImpression");
			}
		}
	}, [inView]);

	const handleVisibilityChange = useCallback(
		(isVisible: boolean) => {
			setWasVisible(isVisible);

			if (isVisible && !parentMetadata?.componentName) {
				sendEvent("BM_componentImpression");
			}
		},
		[sendEvent, parentMetadata?.componentName],
	);

	return <EventTrackingContext.Provider value={value}>{children(props, ref)}</EventTrackingContext.Provider>;
};

/**
 * Custom hook to use the EventTrackingContext
 *
 * @returns - EventTrackingContext
 * @example
 * ```tsx
 * const { sendEvent } = useEventTracking();
 * sendEvent("event_name", { instanceId: "123" });
 * ```
 */
export const useEventTracking = () => useContext(EventTrackingContext);

/**
 * Higher Order Component to wrap a component with Google Analytics context
 *
 * @param metadata - Base event model to be used for the wrapped component
 * @param renderer - Function to render the wrapped component
 * @returns - Wrapped component with Google Analytics context
 * @example
 * ```tsx
 * const EnhancedComponent = withEventTrackingRef({ instanceId: "123" }, (props, ref) => (
 *   <div ref={ref}>My Component</div>
 * ));
 * ```
 */
export function withEventTrackingRef<P, R extends HTMLDivElement>(
	{ withoutVisibilitySensor, ...metadata }: EventMetadata & { withoutVisibilitySensor?: boolean },
	render: (props: P, ref: React.Ref<R>) => ReactNode,
): FC<P> {
	return (props) => (
		<EventTrackingProvider metadata={metadata} disableImpressionTracking={withoutVisibilitySensor} props={props}>
			{render}
		</EventTrackingProvider>
	);
}

/**
 * Higher Order Component to wrap a component with Google Analytics context
 *
 * @param metadata - Base event model to be used for the wrapped component
 * @param Component - Component to be wrapped
 * @returns - Wrapped component with Google Analytics context
 * @example
 * ```tsx
 * const EnhancedComponent = withEventTracking({ instanceId: "123" }, MyComponent);
 * ```
 */
export function withEventTracking<P>(
	{ withoutVisibilitySensor, ...metadata }: EventMetadata & { withoutVisibilitySensor?: boolean },
	Component: FC<P>,
): FC<P> {
	return (props) => (
		<EventTrackingProvider metadata={metadata} disableImpressionTracking={withoutVisibilitySensor}>
			{(_, ref: React.Ref<HTMLDivElement>) => (
				<div ref={ref}>
					<Component {...props} />
				</div>
			)}
		</EventTrackingProvider>
	);
}
